import React, { useState, useEffect } from 'react';
import { Edit3, X, Save, Lock, Mail, User, Shield, CheckCircle2, Eye, EyeOff, Send, Loader2, Trash2, AlertTriangle } from 'lucide-react';
import { UserPermissionProfile, GroupWithPrograms, userManagementService } from '../../services/userManagementService';
import { adminAuthService } from '../../services/adminAuthService';
import { FirebaseAuthService } from '../../services/firebaseAuthService';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  user: UserPermissionProfile | null;
  availableGroups?: GroupWithPrograms[];
  onUserUpdated: () => void;
}

export const EditarUsuarioModal: React.FC<Props> = ({
  isOpen,
  onClose,
  user,
  availableGroups = [],
  onUserUpdated
}) => {
  const [name, setName] = useState('');
  const [login, setLogin] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<number>(2);
  const [status, setStatus] = useState<'Y' | 'N'>('Y');
  
  const [salvando, setSalvando] = useState(false);
  const [excluindo, setExcluindo] = useState(false);
  const [confirmandoExclusao, setConfirmandoExclusao] = useState(false);
  const [enviandoReset, setEnviandoReset] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [sucesso, setSucesso] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && user) {
      setName(user.name || '');
      setLogin(user.login || '');
      setEmail(user.email || '');
      setPassword('');
      setShowPassword(false);
      setStatus(user.active === 'N' || user.active === 'inativo' ? 'N' : 'Y');
      setConfirmandoExclusao(false);
      
      const userGroupId = user.groups && user.groups.length > 0 ? user.groups[0].id : 2;
      setSelectedGroup(userGroupId);
      setErro(null);
      setSucesso(null);
    }
  }, [isOpen, user]);

  if (!isOpen || !user) return null;

  const handleExcluirUsuario = async () => {
    if (!user) return;
    setExcluindo(true);
    setErro(null);
    setSucesso(null);

    try {
      const res = await userManagementService.excluirUsuario(user.id, user.email, user.auth_user_id || undefined);
      if (res.success) {
        setSucesso('Usuário excluído e acesso revogado com sucesso!');
        setTimeout(() => {
          onUserUpdated();
          onClose();
        }, 700);
      } else {
        setErro(res.message || 'Falha ao excluir o usuário.');
      }
    } catch (err: any) {
      setErro(err.message || 'Erro ao processar exclusão.');
    } finally {
      setExcluindo(false);
      setConfirmandoExclusao(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setErro('O campo Nome é obrigatório.');
      return;
    }

    if (password && password.length < 6) {
      setErro('A nova senha deve conter no mínimo 6 caracteres.');
      return;
    }

    setSalvando(true);
    setErro(null);
    setSucesso(null);

    try {
      const res = await adminAuthService.atualizarUsuario(user.id, {
        name: name.trim(),
        login: login.trim() || undefined,
        email: email.trim(),
        password: password.trim() ? password.trim() : undefined,
        groupIds: [selectedGroup],
        active: status
      });

      if (res.success) {
        setSucesso('Usuário atualizado com sucesso!');
        setTimeout(() => {
          onUserUpdated();
          onClose();
        }, 600);
      } else {
        setErro(res.message || 'Falha ao atualizar dados do usuário.');
      }
    } catch (err: any) {
      setErro(err.message || 'Erro ao processar atualização.');
    } finally {
      setSalvando(false);
    }
  };

  const handleSendPasswordResetLink = async () => {
    const targetEmail = email.trim() || user.email?.trim();
    if (!targetEmail) {
      setErro('O usuário não possui um endereço de e-mail cadastrado.');
      return;
    }

    setEnviandoReset(true);
    setErro(null);
    setSucesso(null);

    try {
      await FirebaseAuthService.sendPasswordReset(targetEmail);
      setSucesso(`Link para cadastrar/redefinir senha enviado com sucesso para ${targetEmail}!`);
    } catch (err: any) {
      if (err?.code === 'auth/user-not-found') {
        setErro('Este e-mail ainda não existe no Firebase Auth. Crie o acesso ou cadastre o usuário.');
      } else {
        setErro(err.message || 'Erro ao enviar e-mail de redefinição de senha.');
      }
    } finally {
      setEnviandoReset(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-xs flex items-center justify-center p-2 sm:p-4 md:p-6 overflow-y-auto">
      <div className="bg-slate-900 border border-slate-700/80 rounded-2xl sm:rounded-3xl w-full max-w-lg max-h-[94vh] flex flex-col shadow-2xl overflow-hidden text-left animate-in fade-in zoom-in-95 duration-150 my-auto">
        
        {/* Cabeçalho do Modal */}
        <div className="p-4 sm:p-5 border-b border-slate-800 flex items-center justify-between bg-slate-950/70 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-indigo-600/20 text-indigo-400 flex items-center justify-center border border-indigo-500/30 shrink-0">
              <Edit3 className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-sm sm:text-base font-black text-white">Editar Usuário</h2>
              <p className="text-xs text-slate-400">
                Atualização de Dados, Perfil de Acesso & Status
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-white rounded-xl hover:bg-slate-800 transition cursor-pointer shrink-0"
            title="Fechar modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Formulário */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0 overflow-hidden">
          <div className="p-4 sm:p-5 space-y-4 overflow-y-auto flex-1 min-h-0">
            {erro && (
              <div className="p-3 rounded-xl bg-rose-950/80 border border-rose-800 text-rose-300 text-xs font-bold">
                {erro}
              </div>
            )}

            {sucesso && (
              <div className="p-3 rounded-xl bg-emerald-950/80 border border-emerald-800 text-emerald-300 text-xs font-bold flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" />
                {sucesso}
              </div>
            )}

          {/* Nome */}
          <div>
            <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">
              Nome
            </label>
            <div className="relative">
              <User className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ex: João Silva"
                className="w-full pl-9 pr-3.5 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-white text-xs focus:border-indigo-500 outline-none"
              />
            </div>
          </div>

          {/* E-mail */}
          <div>
            <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1 flex items-center justify-between">
              <span>E-mail</span>
              <span className="text-[10px] text-slate-500 font-normal">
                Recomendado para recuperação de senha
              </span>
            </label>
            <div className="relative">
              <Mail className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="joao@empresa.com"
                className="w-full pl-9 pr-3.5 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-white text-xs focus:border-indigo-500 outline-none"
              />
            </div>
            <p className="text-[10px] text-slate-500 mt-1.5">
              Certifique-se de manter um e-mail válido para permitir que o usuário recupere sua senha via "Esqueci minha senha".
            </p>
          </div>

          {/* Nova Senha */}
          <div>
            <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1 flex items-center justify-between">
              <span>Nova Senha</span>
              <span className="text-[10px] text-slate-500 font-normal">
                Deixe em branco para manter a senha atual
              </span>
            </label>
            <div className="relative">
              <Lock className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Digite a nova senha (mínimo 6 caracteres)"
                className="w-full pl-9 pr-10 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-white text-xs focus:border-indigo-500 outline-none font-mono"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {email && (
              <div className="mt-1.5 flex justify-end">
                <button
                  type="button"
                  disabled={enviandoReset}
                  onClick={handleSendPasswordResetLink}
                  className="text-[11px] text-indigo-400 hover:text-indigo-300 font-semibold flex items-center gap-1 transition cursor-pointer disabled:opacity-50"
                >
                  {enviandoReset ? (
                    <>
                      <Loader2 className="w-3 h-3 animate-spin" />
                      <span>Enviando link...</span>
                    </>
                  ) : (
                    <>
                      <Send className="w-3 h-3" />
                      <span>Enviar link de cadastro/redefinição de senha para este e-mail</span>
                    </>
                  )}
                </button>
              </div>
            )}
          </div>

          {/* Perfil & Status */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1 flex items-center gap-1">
                <Shield className="w-3.5 h-3.5 text-indigo-400" />
                Perfil / Grupo
              </label>
              <select
                value={selectedGroup}
                onChange={(e) => setSelectedGroup(Number(e.target.value))}
                className="w-full px-3 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-white text-xs focus:border-indigo-500 outline-none"
              >
                {availableGroups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">
                Status
              </label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as 'Y' | 'N')}
                className="w-full px-3 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-white text-xs focus:border-indigo-500 outline-none"
              >
                <option value="Y">Ativo (Permitido)</option>
                <option value="N">Inativo (Bloqueado)</option>
              </select>
            </div>
          </div>

          {status === 'N' && (
            <div className="bg-amber-950/40 border border-amber-500/30 rounded-xl p-3 text-xs text-amber-300 flex items-start gap-2.5">
              <span className="text-base leading-none">⚠️</span>
              <div>
                <strong className="font-semibold block text-amber-200">Aviso de Inativação:</strong>
                O usuário terá o login bloqueado. Ao tentar acessar o sistema, verá uma notificação para entrar em contato com a transportadora para regularizar o acesso.
              </div>
            </div>
          )}

          {/* Zona de Exclusão */}
          <div className="pt-2 border-t border-slate-800/80">
            {confirmandoExclusao ? (
              <div className="p-3.5 rounded-xl bg-rose-950/60 border border-rose-800 space-y-2.5">
                <div className="flex items-start gap-2 text-rose-300 text-xs">
                  <AlertTriangle className="w-4 h-4 text-rose-400 shrink-0 mt-0.5" />
                  <div>
                    <span className="font-bold block text-rose-200">Tem certeza que deseja excluir este usuário?</span>
                    O usuário será excluído do sistema e seu acesso revogado permanentemente.
                  </div>
                </div>
                <div className="flex items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setConfirmandoExclusao(false)}
                    className="px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-400 hover:text-white bg-slate-800 hover:bg-slate-700 transition"
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    disabled={excluindo}
                    onClick={handleExcluirUsuario}
                    className="px-3.5 py-1.5 rounded-lg text-xs font-bold text-white bg-rose-600 hover:bg-rose-700 transition flex items-center gap-1.5 shadow-md shadow-rose-900/40 disabled:opacity-50"
                  >
                    {excluindo ? (
                      <>
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        <span>Excluindo...</span>
                      </>
                    ) : (
                      <>
                        <Trash2 className="w-3.5 h-3.5" />
                        <span>Confirmar Exclusão</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <div className="text-[11px] text-slate-500">
                  Deseja remover este usuário permanentemente?
                </div>
                <button
                  type="button"
                  onClick={() => setConfirmandoExclusao(true)}
                  className="px-3 py-1.5 rounded-xl text-xs font-bold text-rose-400 hover:text-rose-300 hover:bg-rose-950/50 border border-rose-900/60 transition flex items-center gap-1.5"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  <span>Excluir Usuário</span>
                </button>
              </div>
            )}
          </div>

          </div>

          {/* Botões Cancelar e Salvar */}
          <div className="p-4 sm:p-5 border-t border-slate-800 bg-slate-950 flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-2.5 shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 rounded-xl text-xs font-bold text-slate-400 hover:text-white hover:bg-slate-800 transition cursor-pointer text-center"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={salvando || excluindo}
              className="px-6 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold shadow-lg shadow-indigo-600/30 flex items-center justify-center gap-2 transition disabled:opacity-50 cursor-pointer"
            >
              <Save className="w-4 h-4 shrink-0" />
              <span>{salvando ? 'Salvando...' : 'Salvar'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
